import { useState, useEffect, useCallback } from "react";
import { Box, Button, Link } from "@mui/material";
import { CookService } from "../../services/CookServices";
import ChefForPartyService from "../../services/ChefForPartyService";
import CallButton from "../Common/CallButton/CallButton";
import { UserType } from "../../enums/common";
import { formatDate } from "../../utils/DateTimeUtils";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

const cookService = new CookService();
const partyService = new ChefForPartyService();

interface InputProps {
    cookId: number;
    party?: boolean;
}

const CookOmsDetailsTab = ({ cookId, party }: InputProps) => {
    const [subData, setSubData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

    const refreshData = useCallback(
        (pageNo: number) => {
            setIsLoading(true);
            if (!party) {
                cookService
                    .getOneMealsForCook(cookId, pageSize, pageSize * pageNo, { order: sortOrder, field: "id" })
                    .then((data) => { setSubData(data); setIsLoading(false); })
                    .catch(() => { setIsLoading(false); });
            } else {
                partyService
                    .fetchAllChefForPartyForCook(pageSize, pageSize * pageNo, { cookId: cookId }, { order: sortOrder, field: "id" })
                    .then((data) => { setSubData(data); setIsLoading(false); })
                    .catch(() => { setIsLoading(false); });
            }
        },
        [cookId, pageSize, party, sortOrder]
    );

    useEffect(() => { refreshData(page); }, [refreshData, page]);

    const rows: any[] = Array.isArray(subData?.data) ? subData.data : [];
    const total = subData?.count ?? 0;

    const getMainChef = (row: any) => row?.cooks?.find((cook: any) => cook?.type === "MAIN_CHEF");

    const getAddress = (row: any) =>
        (party
            ? [row?.house?.address, row?.house?.locality, row?.house?.city]
            : [row?.localityName, row?.areaName, row?.cityName]
        ).filter(Boolean).join(", ") || "Not Available";

    const columns: CommonListColumn[] = [
        { label: 'ID', field: 'id' },
        { label: 'Customer Name', render: (row) => row?.customerName ?? "-" },
        {
            label: 'Customer Address',
            minWidth: 240,
            render: (row) => getAddress(row),
        },
        {
            label: 'Cook Id',
            render: (row) => party ? getMainChef(row)?.details?.cookId ?? "---" : row?.cookId ?? "---",
        },
        {
            label: 'Cook Name',
            render: (row) => party ? getMainChef(row)?.details?.cookName ?? "---" : row?.cookName ?? "---",
        },
        {
            label: 'Cook Mobile',
            stopRowClick: true,
            render: (row) => {
                if (party) {
                    const mainChef = getMainChef(row);
                    return mainChef?.details?.cookPhone ? maskPhoneNumber(mainChef.details.cookPhone) : "---";
                }
                return row?.cookPhoneNumber ? (
                    <CallButton
                        toPhoneNumber={row?.cookPhoneNumber}
                        userType={UserType.COOK}
                        referenceId={row?.id}
                        referenceModelName={"ONE_MEAL_SERVICE"}
                    />
                ) : "---";
            },
        },
        { label: 'Date', nowrap: true, render: (row) => row?.date ? formatDate(row.date) : "-" },
        { label: 'Time', render: (row) => row?.time ?? "-" },
        { label: 'Status', render: (row) => row?.status ?? "-" },
        ...(!party ? [{ label: 'Payment Status', render: (row: any) => row?.paymentStatus ?? "-" } as CommonListColumn] : []),
        {
            label: 'Action',
            stopRowClick: true,
            render: (row) => (
                <Link href={party ? `/#/ChefForParty/${row?.id}/show` : `/#/OneMealService/${row?.id}/show`} underline="none">
                    <Button variant="contained" color="primary" size="small">Show</Button>
                </Link>
            ),
        },
    ];

    return (
        <Box>
            <Box display="flex" justifyContent="flex-end" mb={1}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                        setSortOrder((prev) => (prev === "DESC" ? "ASC" : "DESC"));
                        setPage(0);
                    }}
                >
                    Sort ID: {sortOrder === "DESC" ? "↓ Desc" : "↑ Asc"}
                </Button>
            </Box>
            <CommonListTable
                title={party ? "Parties" : "One Meal Services"}
                columns={columns}
                data={rows}
                loading={isLoading}
                page={page}
                rowsPerPage={pageSize}
                total={total}
                onPageChange={(p) => setPage(p)}
                onRowsPerPageChange={(rows) => { setPageSize(rows); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                emptyText={party ? "No Party found" : "No One Meal Service found"}
            />
        </Box>
    );
};

export default CookOmsDetailsTab;
